import { z } from 'zod';

import { LOCALES } from '../enums.js';

export const UserStatusSchema = z.enum(['active', 'invited', 'disabled']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

/**
 * The statuses an ADMIN may set through the API — deliberately without
 * `invited`.
 *
 * `invited` is a dead state: `users.password_hash` is NOT NULL (so an invited
 * user always has *some* hash) and login only ever accepts `status = 'active'`.
 * The value only exists because `MandobService.create` still writes it, and
 * every row carrying it is a user who looks fine in the list and can never sign
 * in. Read models keep accepting it (those rows exist); the write surface must
 * not be able to create more.
 */
export const UserManageStatusSchema = z.enum(['active', 'disabled']);
export type UserManageStatus = z.infer<typeof UserManageStatusSchema>;

/**
 * The platform-side `user_email_lookup.email` column (email → tenant index that
 * login resolves through) is varchar(254). A longer address would insert fine
 * into `users.email` (varchar(320)) and then blow up claiming the lookup row, so
 * the cap is enforced here — at the edge — for every surface that creates a user.
 */
const EMAIL_MAX = 254;

/** Phone is free-form (Iraqi numbers arrive in many shapes); we only bound it. */
const PhoneSchema = z.string().trim().max(40);

export const UserSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  email: z.string().email().toLowerCase(),
  fullName: z.string().min(1).max(200),
  phone: z.string().nullable().default(null),
  locale: z.enum(LOCALES),
  status: UserStatusSchema,
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type User = z.infer<typeof UserSchema>;

export const UserCreateSchema = z.object({
  email: z.string().email().max(EMAIL_MAX).toLowerCase(),
  fullName: z.string().min(1).max(200),
  phone: PhoneSchema.optional(),
  locale: z.enum(LOCALES).default('en'),
  /**
   * Optional starting password. Omit it and the server generates a temporary
   * one and returns it ONCE (`UserCreated.tempPassword`) for staff to hand over
   * — it is never stored or logged in plaintext. Either way the user ends up
   * `active` with a real argon2id hash, i.e. able to actually sign in.
   */
  password: z.string().min(8).max(200).optional(),
  roleIds: z.array(z.string().uuid()).min(1),
});

export type UserCreate = z.infer<typeof UserCreateSchema>;

export const UserUpdateSchema = z.object({
  fullName: z.string().min(1).max(200).optional(),
  /**
   * The SIGN-IN IDENTITY. Login resolves an address to a tenant through the
   * PLATFORM-side `user_email_lookup` table, which on a split tenant lives in a
   * different database from `users` — so changing this has to move two rows in
   * two databases, in the right order. See `UsersController.patch`.
   *
   * The 254 cap is not cosmetic: `user_email_lookup.email` is varchar(254)
   * while `users.email` is varchar(320), so a longer address would update
   * tenant-side and then explode claiming the lookup.
   */
  email: z.string().email().max(EMAIL_MAX).toLowerCase().optional(),
  phone: PhoneSchema.nullable().optional(),
  locale: z.enum(LOCALES).optional(),
  /** Active/Disabled only — see UserManageStatusSchema for why `invited` is out. */
  status: UserManageStatusSchema.optional(),
  roleIds: z.array(z.string().uuid()).optional(),
  /**
   * Which warehouses this person may WRITE against. An empty array clears the
   * restriction — no rows means every warehouse, the same convention
   * pos_terminal_users uses — so `[]` and "unrestricted" are the same thing
   * here, and omitting the field leaves the restriction untouched.
   */
  warehouseIds: z.array(z.string().uuid()).optional(),
});

export type UserUpdate = z.infer<typeof UserUpdateSchema>;

/**
 * Body of `POST /users/:id/password` (an admin setting SOMEONE ELSE's password).
 * Omit `password` and the server generates one. Never valid for your own
 * account — that path requires the current password (`POST /auth/change-password`).
 */
export const UserPasswordSetSchema = z.object({
  password: z.string().min(8).max(200).optional(),
});

export type UserPasswordSet = z.infer<typeof UserPasswordSetSchema>;

/**
 * `POST /users/:id/password` response. The plaintext is returned exactly ONCE
 * and is never stored, audited or logged.
 */
export interface UserPasswordSetResult {
  email: string;
  password: string;
}

/**
 * Why a user cannot sign in, computed by comparing the tenant `users` row with
 * the platform `user_email_lookup` row (two databases — set membership in JS,
 * never a join):
 *  • `no_lookup`      — no platform row at all; nothing can route a login here.
 *  • `email_mismatch` — the platform row points at a different address.
 *  • `invited`        — status is `invited`; login only accepts `active`.
 *
 * `disabled` is NOT an issue — that is a deliberate, reversible admin decision.
 */
export type SignInIssue = 'no_lookup' | 'email_mismatch' | 'invited';

/** A user as the list/get/create/update endpoints return them. */
export interface UserWithRoles extends User {
  /** Role NAMES, for display. */
  roles: string[];
  /**
   * Role IDS, for the edit dialog's prefill. Names are editable and non-unique,
   * so they cannot be matched back to ids. Optional so a client can tell "the
   * server didn't say" apart from "this user has no roles" — a blank picker
   * must never be mistaken for "remove all their roles".
   */
  roleIds?: string[];
  /** Non-null when this user's sign-in is broken; see SignInIssue. */
  signInIssue?: SignInIssue | null;
}

/**
 * `POST /users` response. `tempPassword` is populated ONLY when the server
 * generated the password (the caller didn't supply one) — it is the single
 * chance to read it, so the UI must surface it immediately.
 */
export interface UserCreated extends UserWithRoles {
  tempPassword: string | null;
}
